import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Shield, KeyRound } from 'lucide-react';
import { verifyMFA } from '../lib/api';

const MFAVerify = ({ mfaToken, onSuccess, onCancel, userName }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code) {
      setError('Please enter a code');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await verifyMFA(mfaToken, code);
      onSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1 text-center">
        <div className="flex justify-center mb-2">
          <Shield className="h-12 w-12 text-primary" />
        </div>
        <CardTitle className="text-2xl">Two-Factor Authentication</CardTitle>
        <CardDescription>
          {userName && <span className="block mb-1">Welcome back, {userName}</span>}
          Enter the verification code from your authenticator app
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="code">
              {useBackupCode ? 'Backup Code' : '6-Digit Code'}
            </Label>
            <Input
              id="code"
              type="text"
              placeholder={useBackupCode ? 'Enter backup code' : '000000'}
              value={code}
              onChange={(e) => setCode(useBackupCode ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ''))}
              maxLength={useBackupCode ? 8 : 6}
              className={useBackupCode ? '' : 'text-center text-2xl tracking-widest'}
              autoComplete="one-time-code"
              autoFocus
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify'
            )}
          </Button>

          <div className="flex flex-col gap-2 text-center">
            <Button
              type="button"
              variant="link"
              onClick={() => {
                setUseBackupCode(!useBackupCode);
                setCode('');
                setError('');
              }}
              className="text-sm"
            >
              <KeyRound className="h-4 w-4 mr-1" />
              {useBackupCode ? 'Use authenticator app instead' : "Use a backup code"}
            </Button>

            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                onClick={onCancel}
                className="text-sm text-muted-foreground"
              >
                Cancel and sign out
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default MFAVerify;
